import { createSelector, EntityId } from "@reduxjs/toolkit";
import { DateTime } from "luxon";
import { persistedRecordingAdapter, ReIdState } from "./state";
import { RootState } from "app/store";
import { playbackSelectors } from "features/cases/caseViewer/store/selectors.playback";
import { caseViewerSelectors } from "features/cases/caseViewer/store/selectors";
import { ObjectDetectionFrame } from "types/reId/ObjectDetectionFrame";
import { ObjectDetectionFrameWithLabelName } from "types/reId/ObjectDetectionFrameWithLabelName";
import { RecordingPlaybackDirection } from "features/cases/caseViewer/types/RecordingPlaybackDirection";
import { DossierDevice } from "types/DossierDevice";
import { UserPlayMode } from "features/playback/types/UserPlayMode";
import { Dossier } from "types/Dossier";
import { DossierCapabilityName } from "types/DossierCapabilityName";
import { ObjectDetectionModelName } from "types/ObjectDetectionModelName";

const selectReId = (state: RootState) => state.reId;

const selectActiveDossierDeviceId = createSelector(
    [selectReId],
    (state: ReIdState) => state.activeDossierDeviceId,
);

const selectActivePersistedRecordingId = createSelector(
    [selectReId],
    (state: ReIdState) => state.activePersistedRecordingId,
);

const selectReIdViewModeIsActive = createSelector(
    [selectReId],
    (state: ReIdState) => state.reIdViewModeIsActive,
);

const selectSelectedObjectDetectionModelName = createSelector(
    [selectReId],
    (state: ReIdState) => state.selectedObjectDetectionModelName,
);

const selectShouldFetchFetchObjectDetections = createSelector(
    [
        selectReIdViewModeIsActive,
        playbackSelectors.selectUserRequestedPlayMode,
        caseViewerSelectors.selectDossier,
        playbackSelectors.selectPlaybackStartDateTimeChangedTimestamp,
    ],
    (
        reIdViewModeIsActive: boolean,
        userRequestedPlayMode: UserPlayMode,
        dossier?: Dossier,
        playbackStartDateTimeChangedTimestamp?: number,
    ) => {
        const shouldFetch =
            dossier &&
            dossier.dossierCapabilityNames.some(
                (x) => x === DossierCapabilityName.ReIdentification,
            );

        return shouldFetch
            ? {
                  enableInterval:
                      userRequestedPlayMode === "play" ||
                      userRequestedPlayMode === "resume",
                  dossierId: dossier?.id,
                  playbackStartDateTimeChangedTimestamp:
                      playbackStartDateTimeChangedTimestamp,
              }
            : undefined;
    },
);

const selectDetectedObjects = createSelector(
    [selectReId, selectSelectedObjectDetectionModelName],
    (
        state: ReIdState,
        selectedObjectDetectionModelName: ObjectDetectionModelName | undefined,
    ) => {
        if (!selectedObjectDetectionModelName) {
            return [];
        }

        return (
            state.detectedObjects.filter(
                (x) =>
                    x.objectDetectionModelName ===
                    state.selectedObjectDetectionModelName,
            ) ?? []
        );
    },
);

const selectObjectDetectionModels = createSelector(
    [selectReId],
    (state: ReIdState) => state.objectDetectionModels ?? [],
);

const selectReIdentificationSelectedObjectDetectionModelNamesDossierCapabilityConfigurationSetting =
    createSelector(
        [selectReId],
        (state: ReIdState) =>
            state.reIdentificationModelSelectionDossierCapabilitySetting,
    );

const selectSelectedDetectedObjectFrame = createSelector(
    [selectReId],
    (state: ReIdState) => state.selectedDetectedObjectFrame,
);

const { selectAll: selectAllActiveDetectedObjectPersistedRecordings } =
    persistedRecordingAdapter.getSelectors(
        (state: RootState) =>
            state.reId.activeDetectedObjectPersistedRecordings,
    );

const selectActiveVectorSearchResultRecordToPlay = createSelector(
    [selectReId],
    (state: ReIdState) => state.activeVectorSearchResultRecordToPlay,
);

const selectSavedUserPlaybackMode = createSelector(
    [selectReId],
    (state: ReIdState) => state.savedUserPlaybackMode,
);

const selectHasObjectDetectionFramesForPersistedRecording = createSelector(
    [
        selectDetectedObjects,
        (state: RootState, persistedRecordingId: EntityId) =>
            persistedRecordingId,
    ],
    (objectDetectionFrames, persistedRecordingId) =>
        (objectDetectionFrames ?? []).some(
            (x) => x.persistedRecordingId === persistedRecordingId,
        ),
);

const selectAllObjectDetectionsByPersistedRecordingIdAndTimeFactory = (
    direction: RecordingPlaybackDirection,
) => {
    const selectCurrentTimeByPersistedRecordingId =
        playbackSelectors.selectCurrentTimeByPersistedRecordingIdOnEveryTickFactory(
            direction,
        );

    // Recomputes ONLY when detections or recording id change — not per tick.
    const selectFramesForRecording = createSelector(
        [
            selectDetectedObjects,
            (state: RootState, persistedRecordingId: EntityId) =>
                persistedRecordingId,
        ],
        (objectDetectionFrames, persistedRecordingId) =>
            (objectDetectionFrames ?? []).filter(
                (x) => x.persistedRecordingId === persistedRecordingId,
            ),
    );
    return createSelector(
        [
            selectCurrentTimeByPersistedRecordingId,
            selectFramesForRecording,
            selectObjectDetectionModels,
        ],
        (
            currentTimeInPersistedRecording,
            framesForRecording,
            objectDetectionModels,
        ) => {
            const currentTime = currentTimeInPersistedRecording.currentTime;
            const timeToleranceInSeconds = 0.5;

            // closest frame per trackerId, but only within the time window
            const closestFrameByTrackerId = new Map<
                number,
                ObjectDetectionFrame
            >();

            for (const frame of framesForRecording) {
                // Skip detections that are outside the time tolerance window
                const delta = Math.abs(frame.videoTimeSec - currentTime);
                if (delta > timeToleranceInSeconds) {
                    continue;
                }

                const existing = closestFrameByTrackerId.get(frame.trackerId);
                if (
                    !existing ||
                    delta < Math.abs(existing.videoTimeSec - currentTime)
                ) {
                    closestFrameByTrackerId.set(frame.trackerId, frame);
                }
            }
            const result = Array.from(closestFrameByTrackerId.values()).sort(
                (a, b) => a.trackerId - b.trackerId,
            );

            const resultWithLabelName: ObjectDetectionFrameWithLabelName[] =
                result.map((frame) => ({
                    ...frame,
                    labelName:
                        objectDetectionModels
                            .find(
                                (model) =>
                                    model.name ===
                                    frame.objectDetectionModelName,
                            )
                            ?.classes?.find(
                                (modelClass) =>
                                    modelClass.classId === frame.classId,
                            )?.labelName ?? frame.labelName,
                }));

            return resultWithLabelName;
        },
    );
};

const calculateAbsoluteFirstSeenTime = (
    persistedStartTimeWithFirstSeenTime: string,
): number => {
    return DateTime.fromISO(persistedStartTimeWithFirstSeenTime).toMillis();
};

const selectVectorSearchResults = createSelector(
    [selectReId, caseViewerSelectors.selectSelectedDossierDevices],
    (state: ReIdState, selectedDossierDevices: DossierDevice[]) => {
        const vectorSearchResults = state.vectorSearchResults ?? [];
        const selectedDossierDeviceNameById = new Map(
            selectedDossierDevices.map((device) => [device.id, device.name]),
        );
        return vectorSearchResults
            .map((vectorSearchResult) => {
                return {
                    ...vectorSearchResult,
                    dossierDeviceName:
                        selectedDossierDeviceNameById.get(
                            vectorSearchResult.dossierDeviceId,
                        ) ?? "Unknown device",
                    absoluteFirstSeenTimeMillis: calculateAbsoluteFirstSeenTime(
                        vectorSearchResult.persistedStartTimeWithFirstSeenTime,
                    ),
                };
            })
            .sort(
                (a, b) =>
                    a.absoluteFirstSeenTimeMillis -
                    b.absoluteFirstSeenTimeMillis,
            );
    },
);

const selectLoadingVectorSearchResults = createSelector(
    [selectReId],
    (state: ReIdState) => state.loadingVectorSearchResults,
);

export const reIdSelectors = {
    selectReId,
    selectActiveDossierDeviceId,
    selectReIdViewModeIsActive,
    selectDetectedObjects,
    selectSelectedDetectedObjectFrame,
    selectObjectDetectionModels,
    selectReIdentificationSelectedObjectDetectionModelNamesDossierCapabilityConfigurationSetting,
    selectSelectedObjectDetectionModelName,
    selectActivePersistedRecordingId,
    selectActiveVectorSearchResultRecordToPlay,
    selectAllObjectDetectionsByPersistedRecordingIdAndTimeFactory,
    selectAllActiveDetectedObjectPersistedRecordings,
    selectVectorSearchResults,
    selectShouldFetchFetchObjectDetections,
    selectSavedUserPlaybackMode,
    selectLoadingVectorSearchResults,
    selectHasObjectDetectionFramesForPersistedRecording,
};
